<template>
  <div class="pp-tab-surface">
    <div class="pp-loc">
      <div class="map-shell">
        <PropertiesMap
          v-if="hasValidCoordinates"
          :center-by-marker="marker"
          default-marker
          :zoom="14"
          :show-controls="false"
          :points-of-interest="poiMarkers"
          :focused-poi="focusedPoi"
        />
        <div
          v-else
          class="pp-map-placeholder"
        >
          {{ t('property.locationTab.mapUnavailable') }}
        </div>
      </div>
      <div class="pp-poi-card">
        <h4>{{ t('property.v2Location.nearbyPoi') }}</h4>
        <div
          v-if="isLoading"
          class="loading"
        >
          <div
            v-for="i in 5"
            :key="i"
            class="skel"
          ></div>
        </div>
        <template v-else-if="nearbyLocations.length">
          <div
            v-if="categoryChips.length > 1"
            class="filters"
          >
            <label
              for="poi-category"
              class="filter-label"
              >{{ t('property.v2Location.filterBy') }}</label
            >
            <select
              id="poi-category"
              v-model="activeCategory"
              class="filter-select"
            >
              <option
                v-for="chip in categoryChips"
                :key="chip.value"
                :value="chip.value"
              >
                {{ chip.label }} ({{ chip.count }})
              </option>
            </select>
          </div>
          <div class="rows">
            <button
              v-for="(loc, idx) in filteredLocations"
              :key="`${loc.name}-${idx}`"
              type="button"
              class="row"
              :class="{ active: focusedKey === buildPoiKey(loc) }"
              @click="onPoiClick(loc)"
            >
              <div
                class="ic"
                aria-hidden="true"
              >
                <UIIcon
                  name="location-outline"
                  size="sm"
                />
              </div>
              <div class="body">
                <div class="n">{{ loc.name }}</div>
                <div class="meta">
                  <span
                    v-if="loc.type"
                    class="tag"
                    >{{ formatCategory(loc.type) }}</span
                  >
                  <span
                    v-if="loc.distance"
                    class="d"
                    >{{ loc.distance }}</span
                  >
                </div>
              </div>
              <div
                v-if="loc.time"
                class="dist"
              >
                {{ loc.time }}
              </div>
            </button>
          </div>
        </template>
        <div
          v-else
          class="empty"
        >
          {{ t('property.locationTab.noData') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropertyDetailed } from '@/types/properties';
import { useNearbyLocations } from '@/api/queries/properties';

const props = defineProps<{
  item: PropertyDetailed;
}>();

const { t } = useI18n();
const route = useRoute();

const lat = computed(() => Number(props.item?.latitude));
const lng = computed(() => Number(props.item?.longitude));
// (0, 0) is the postgres mapper's "missing coords" sentinel (toNumber returns
// 0 for null). Treat it as invalid so the project page doesn't render the
// world map centered on the Gulf of Guinea for projects whose lat/lon hasn't
// been imported yet.
const hasValidCoordinates = computed(
  () => Number.isFinite(lat.value) && Number.isFinite(lng.value) && !(lat.value === 0 && lng.value === 0),
);
const marker = computed<google.maps.LatLngLiteral | undefined>(() =>
  hasValidCoordinates.value ? { lat: lat.value, lng: lng.value } : undefined,
);

const coordinates = computed(() => (hasValidCoordinates.value ? { latitude: lat.value, longitude: lng.value } : null));

// `route.params.id` only exists on the legacy /properties/<id> route; the
// canonical /projects/<dev>/<slug> route has no id param, so reading it
// here was undefined and the composable's `enabled: !!propertyId` flag
// silently disabled the query. Prefer the resolved property's own id /
// complex_id so the lookup fires on both URL shapes.
const propertyId = computed<string>(() => {
  const p = props.item as { id?: string | number; complex_id?: string | number } | null | undefined;
  return String(p?.id ?? p?.complex_id ?? route.params.id ?? '');
});
const nearbyLocationsQuery = useNearbyLocations(propertyId, coordinates);
const nearbyLocations = computed(() => nearbyLocationsQuery.data.value ?? []);
const isLoading = computed(() => nearbyLocationsQuery.isLoading.value);

const poiMarkers = computed(() => {
  const list = nearbyLocations.value ?? [];
  return list
    .filter(
      (loc): loc is typeof loc & { latitude: number; longitude: number } =>
        loc != null && Number.isFinite(Number(loc.latitude)) && Number.isFinite(Number(loc.longitude)),
    )
    .map((loc) => ({
      lat: Number(loc.latitude),
      lng: Number(loc.longitude),
      title: loc.name ?? '',
    }));
});

// OSM category strings come in as lowercase snake-or-single-word
// ("supermarket", "fast_food", "kindergarten"). Pretty-print for the
// list chip: split on _, title-case each word.
const formatCategory = (raw: string | null | undefined): string => {
  if (!raw) return '';
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

// ── Category filter chips ────────────────────────────────────────
// Build chips from the loaded list itself so each project shows only the
// categories it actually has nearby — no empty "Hospital · 0" buttons.
type Chip = { value: string; label: string; count: number };
const ALL_CATEGORY = '__all__';
const activeCategory = ref<string>(ALL_CATEGORY);

const categoryChips = computed<Chip[]>(() => {
  const counts = new Map<string, number>();
  for (const loc of nearbyLocations.value) {
    const cat = (loc.type ?? '').trim();
    if (!cat) continue;
    counts.set(cat, (counts.get(cat) ?? 0) + 1);
  }
  const chips: Chip[] = [
    { value: ALL_CATEGORY, label: t('property.v2Location.allCategories'), count: nearbyLocations.value.length },
  ];
  for (const [cat, n] of [...counts.entries()].sort((a, b) => b[1] - a[1])) {
    chips.push({ value: cat, label: formatCategory(cat), count: n });
  }
  return chips;
});

const filteredLocations = computed(() =>
  activeCategory.value === ALL_CATEGORY
    ? nearbyLocations.value
    : nearbyLocations.value.filter((loc) => loc.type === activeCategory.value),
);

// ── Click-to-focus on map ────────────────────────────────────────
// Clicking a list row pans + zooms the map to the POI's coords. We pass
// a new `focusedPoi` prop to PropertiesMap whenever the user picks one.
// The same key (lat,lng-rounded) is used to highlight the active row.
const buildPoiKey = (loc: { latitude?: number | null; longitude?: number | null }): string => {
  const lat = Number(loc.latitude);
  const lng = Number(loc.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
  return `${lat.toFixed(5)},${lng.toFixed(5)}`;
};

const focusedKey = ref<string>('');
const focusedPoi = ref<{ lat: number; lng: number } | null>(null);

const onPoiClick = (loc: { latitude?: number | null; longitude?: number | null }) => {
  const lat = Number(loc.latitude);
  const lng = Number(loc.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  focusedKey.value = buildPoiKey(loc);
  // Set null first so re-clicking the same row still triggers the map's
  // watcher (Vue dedupes same-value watch fires).
  focusedPoi.value = null;
  nextTick(() => {
    focusedPoi.value = { lat, lng };
  });
};

// Reset focus when filter changes so we don't show "active row" highlight
// on a row that's just been filtered out of view.
watch(activeCategory, () => {
  focusedKey.value = '';
  focusedPoi.value = null;
});
</script>

<style scoped lang="scss">
.pp-tab-surface {
  background: #f8f7f3;
  border-radius: 20px;
  padding: 28px;
}

.pp-loc {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

.map-shell {
  height: 420px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(13, 15, 21, 0.08);
}

.pp-map-placeholder {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f3f3;
  color: rgba(13, 15, 21, 0.62);
  font-size: 14px;
}

.pp-poi-card {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 16px;
  padding: 18px 20px;
  max-height: 420px;
  overflow-y: auto;

  h4 {
    font-family: 'Fraunces', 'Times New Roman', serif;
    font-weight: 600;
    font-size: 18px;
    letter-spacing: -0.015em;
    margin: 0 0 14px;
  }

  .loading {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .skel {
      height: 44px;
      border-radius: 8px;
      background: #f3f3f3;
      animation: pulse 1.6s ease-in-out infinite;
    }
  }

  .empty {
    color: rgba(13, 15, 21, 0.62);
    font-size: 13px;
    padding: 12px 0;
  }

  // Category dropdown (was a chip strip — converted to a select so dense
  // OSM category sets don't wrap to multiple rows on narrow viewports).
  .filters {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0 10px;
    margin-bottom: 4px;
    border-bottom: 1px solid rgba(13, 15, 21, 0.08);

    .filter-label {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      font-weight: 600;
      color: rgba(13, 15, 21, 0.55);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      flex-shrink: 0;
    }

    .filter-select {
      flex: 1;
      min-width: 0;
      height: 34px;
      padding-block: 0;
      padding-inline: 10px 32px;
      border-radius: 8px;
      border: 1px solid rgba(13, 15, 21, 0.12);
      background-color: white;
      color: #0d0f15;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      // Inline SVG chevron — matches the rest of the v2 UI without pulling
      // in an icon dependency just for this control.
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%230d0f15' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 10px center;
      transition: border-color 0.12s ease;

      // The chevron sits on the inline-end side, matching the padding above.
      [dir='rtl'] & {
        background-position: left 10px center;
      }

      &:hover {
        border-color: rgba(13, 15, 21, 0.3);
      }

      &:focus {
        outline: none;
        border-color: #0d0f15;
      }
    }
  }

  // Row scroll container — caps the list height so the filter chips and
  // map don't get pushed out of view on dense neighbourhoods.
  .rows {
    max-height: 420px;
    overflow-y: auto;
    margin: 0 -4px;
    padding: 0 4px;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(13, 15, 21, 0.08);
    text-align: start;
    background: transparent;
    border-inline-start: none;
    border-inline-end: none;
    border-top: none;
    width: 100%;
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover {
      background: rgba(255, 182, 72, 0.06);
    }

    &.active {
      background: rgba(255, 182, 72, 0.12);
    }

    &:last-child {
      border-bottom: none;
    }

    .ic {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #f8f7f3;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .body {
      flex: 1;
      min-width: 0;

      .n {
        font-size: 13px;
        font-weight: 600;
        color: #0d0f15;
      }

      .meta {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 3px;
        flex-wrap: wrap;
      }

      .tag {
        display: inline-flex;
        align-items: center;
        padding: 1px 7px;
        border-radius: 5px;
        background: rgba(13, 15, 21, 0.05);
        color: rgba(13, 15, 21, 0.7);
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: capitalize;
      }

      .d {
        font-size: 11.5px;
        color: rgba(13, 15, 21, 0.62);
      }
    }

    .dist {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 11px;
      font-weight: 700;
      color: #0d0f15;
      letter-spacing: 0.02em;
      flex-shrink: 0;
    }
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
